import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { PermissionGate } from './components/PermissionGate';
import Layout from './components/Layout';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import Dashboard from './pages/Dashboard';
import Welcome from './pages/Welcome';
import UsersList from './pages/users/UsersList';
import UserDetail from './pages/users/UserDetail';
import InvoicesList from './pages/invoices/InvoicesList';
import InvoiceDetail from './pages/invoices/InvoiceDetail';
import InvoiceForm from './pages/invoices/InvoiceForm';
import AuditLog from './pages/AuditLog';
import XeroMigration from './pages/settings/XeroMigration';
import SystemAdmin from './pages/SystemAdmin';
import AccountsOverview from './pages/accounts/AccountsOverview';
import Transactions from './pages/accounts/Transactions';
import ChartOfAccounts from './pages/accounts/ChartOfAccounts';
import JournalEntries from './pages/accounts/JournalEntries';
import PLReport from './pages/accounts/PLReport';
import BalanceSheet from './pages/accounts/BalanceSheet';
import BankConnections from './pages/accounts/BankConnections';
import PlaidTransactions from './pages/accounts/PlaidTransactions';
import MerchantRules from './pages/accounts/MerchantRules';
import BankImport from './pages/accounts/BankImport';
import PendingTransactions from './pages/accounts/PendingTransactions';
import XeroImport from './pages/accounts/XeroImport';
import TrialBalance from './pages/accounts/TrialBalance';
import ClientsList from './pages/clients/ClientsList';
import SuppliersList from './pages/suppliers/SuppliersList';
import BankAccountsList from './pages/accounts/BankAccountsList';
import AgedReceivables from './pages/accounts/AgedReceivables';
import ExpenseCategorization from './pages/accounts/ExpenseCategorization';
import './App.css';

function PermRoute({ perm, children }) {
  return <PermissionGate permission={perm} redirect>{children}</PermissionGate>;
}

/** "/" and unknown paths: the first screen this user holds, not the dashboard. */
function Landing() {
  const { landingPath } = useAuth();
  return <Navigate to={landingPath()} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<PermRoute perm="dashboard"><Dashboard /></PermRoute>} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/users" element={<ProtectedRoute requiredRole="admin"><UsersList /></ProtectedRoute>} />
                    <Route path="/users/:userId" element={<ProtectedRoute requiredRole="admin"><UserDetail /></ProtectedRoute>} />
                    <Route path="/invoices" element={<PermRoute perm="invoices"><InvoicesList /></PermRoute>} />
                    {/* /invoices/new must precede /invoices/:invoiceId, or "new" is read as an id. */}
                    <Route path="/invoices/new" element={<PermRoute perm="invoices"><InvoiceForm /></PermRoute>} />
                    <Route path="/invoices/:invoiceId/edit" element={<PermRoute perm="invoices"><InvoiceForm /></PermRoute>} />
                    <Route path="/invoices/:invoiceId" element={<PermRoute perm="invoices"><InvoiceDetail /></PermRoute>} />
                    <Route path="/audit-log" element={<ProtectedRoute requiredRole="admin"><AuditLog /></ProtectedRoute>} />
                    <Route path="/contacts" element={<Navigate to="/suppliers" replace />} />
                    <Route path="/suppliers" element={<PermRoute perm="suppliers"><SuppliersList /></PermRoute>} />
                    <Route path="/clients" element={<PermRoute perm="clients"><ClientsList /></PermRoute>} />
                    <Route path="/settings/xero-migration" element={<ProtectedRoute requiredRole="admin"><XeroMigration /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute requiredRole="admin"><XeroMigration /></ProtectedRoute>} />
                    <Route path="/system-admin" element={<ProtectedRoute requiredRole="admin"><SystemAdmin /></ProtectedRoute>} />
                    <Route path="/accounts/bank-accounts" element={<PermRoute perm="accounts_usa"><BankAccountsList /></PermRoute>} />
                    <Route path="/accounts/usa/aged-receivables" element={<PermRoute perm="accounts_usa"><AgedReceivables /></PermRoute>} />
                    <Route path="/accounts/usa/overview" element={<PermRoute perm="accounts_usa"><AccountsOverview /></PermRoute>} />
                    <Route path="/accounts/usa/transactions" element={<PermRoute perm="accounts_usa"><Transactions /></PermRoute>} />
                    <Route path="/accounts/usa/chart-of-accounts" element={<PermRoute perm="accounts_usa"><ChartOfAccounts /></PermRoute>} />
                    <Route path="/accounts/usa/journal-entries" element={<PermRoute perm="accounts_usa"><JournalEntries /></PermRoute>} />
                    <Route path="/accounts/usa/pl-report" element={<PermRoute perm="accounts_usa"><PLReport /></PermRoute>} />
                    <Route path="/accounts/usa/balance-sheet" element={<PermRoute perm="accounts_usa"><BalanceSheet /></PermRoute>} />
                    <Route path="/accounts/usa/bank-connections" element={<PermRoute perm="accounts_usa"><BankConnections /></PermRoute>} />
                    <Route path="/accounts/usa/plaid-transactions" element={<PermRoute perm="accounts_usa"><PlaidTransactions /></PermRoute>} />
                    <Route path="/accounts/usa/merchant-rules" element={<PermRoute perm="accounts_usa"><MerchantRules /></PermRoute>} />
                    <Route path="/accounts/usa/import-transactions" element={<PermRoute perm="accounts_usa"><BankImport /></PermRoute>} />
                    <Route path="/accounts/usa/pending-review" element={<PermRoute perm="accounts_usa"><PendingTransactions /></PermRoute>} />
                    <Route path="/accounts/usa/expenses" element={<PermRoute perm="accounts_usa"><ExpenseCategorization /></PermRoute>} />
                    <Route path="/accounts/usa/xero-import" element={<PermRoute perm="accounts_usa"><XeroImport /></PermRoute>} />
                    <Route path="/accounts/usa/trial-balance" element={<PermRoute perm="accounts_usa"><TrialBalance /></PermRoute>} />
                    <Route path="/accounts/india/overview" element={<PermRoute perm="accounts_india"><AccountsOverview /></PermRoute>} />
                    <Route path="/accounts/india/transactions" element={<PermRoute perm="accounts_india"><Transactions /></PermRoute>} />
                    <Route path="/accounts/india/chart-of-accounts" element={<PermRoute perm="accounts_india"><ChartOfAccounts /></PermRoute>} />
                    <Route path="/accounts/india/journal-entries" element={<PermRoute perm="accounts_india"><JournalEntries /></PermRoute>} />
                    <Route path="/accounts/india/pl-report" element={<PermRoute perm="accounts_india"><PLReport /></PermRoute>} />
                    <Route path="/accounts/india/balance-sheet" element={<PermRoute perm="accounts_india"><BalanceSheet /></PermRoute>} />
                    <Route path="/accounts/india/merchant-rules" element={<PermRoute perm="accounts_india"><MerchantRules /></PermRoute>} />
                    <Route path="/accounts/india/import-transactions" element={<PermRoute perm="accounts_india"><BankImport /></PermRoute>} />
                    <Route path="/accounts/india/pending-review" element={<PermRoute perm="accounts_india"><PendingTransactions /></PermRoute>} />
                    <Route path="/accounts/india/expenses" element={<PermRoute perm="accounts_india"><ExpenseCategorization /></PermRoute>} />
                    <Route path="/" element={<Landing />} />
                    <Route path="*" element={<Landing />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
